import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { formatCurrency } from '@/lib/utils';
import type { DiceConfig, DiceRollResult } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

// Pip positions (x, y as percentages of 100x100 viewBox)
const PIPS: Record<number, [number, number][]> = {
    1: [[50, 50]],
    2: [[28, 28], [72, 72]],
    3: [[28, 28], [50, 50], [72, 72]],
    4: [[28, 28], [72, 28], [28, 72], [72, 72]],
    5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
    6: [[28, 22], [72, 22], [28, 50], [72, 50], [28, 78], [72, 78]],
};

function DieFace({ value, won }: { value: number; won: boolean }) {
    const pips = PIPS[value];
    const accent = won ? '#4ade80' : '#f87171';
    return (
        <svg viewBox="0 0 100 100" width="48" height="48" className="shrink-0">
            <rect x="5" y="5" width="90" height="90" rx="16" fill="#1e293b" stroke={accent} strokeWidth="4" />
            {pips
                ? pips.map(([cx, cy], i) => (
                    <circle key={i} cx={cx} cy={cy} r="9" fill="white" />
                ))
                : (
                    <text x="50" y="65" textAnchor="middle" fill="white" fontSize="44" fontWeight="bold">
                        {value}
                    </text>
                )}
        </svg>
    );
}

const WIN_CONDITION_LABELS: Record<string, string> = {
    above: 'Roll above',
    below: 'Roll below',
    exact: 'Roll exactly',
    doubles: 'Roll doubles',
    evens: 'Roll even total',
    odds: 'Roll odd total',
};

const DICE_TYPE_SIDES: Record<string, number> = {
    d4: 4, d6: 6, d8: 8, d10: 10, d12: 12, d20: 20,
};

const MODE_LABELS: Record<string, string> = {
    non_cumulative: 'Standard',
    cumulative: 'Cumulative (vs Dom)',
    unlimited: 'Unlimited',
};

export function DiceRoll({ contractId, isDom }: { contractId: number; isDom: boolean }) {
    const queryClient = useQueryClient();
    const [showCreate, setShowCreate] = useState(false);
    const [rollResult, setRollResult] = useState<{ configId: number; result: DiceRollResult } | null>(null);
    const [rolling, setRolling] = useState(false);
    const [form, setForm] = useState({
        name: '',
        num_dice: '2',
        num_sides: '6',
        dice_type: 'd6' as string,
        win_condition: 'above',
        win_threshold: '7',
        loss_amount: '10',
        win_reward: '5',
        cooldown_minutes: '60',
        mode: 'non_cumulative' as string,
        multiplier_amount: '',
    });

    const { data: diceData } = useQuery({
        queryKey: ['dice', contractId],
        queryFn: () => api.get<{ data: DiceConfig[] }>(`/contracts/${contractId}/dice`),
    });

    const createMutation = useMutation({
        mutationFn: (data: unknown) => api.post(`/contracts/${contractId}/dice`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dice', contractId] });
            setShowCreate(false);
            setForm({ name: '', num_dice: '2', num_sides: '6', dice_type: 'd6', win_condition: 'above', win_threshold: '7', loss_amount: '10', win_reward: '5', cooldown_minutes: '60', mode: 'non_cumulative', multiplier_amount: '' });
        },
    });

    const rollMutation = useMutation({
        mutationFn: (diceId: number) => api.post<{ data: DiceRollResult }>(`/contracts/${contractId}/dice/${diceId}/roll`, {}),
        onSuccess: (res, diceId) => {
            trackEvent('dice_rolled', { contract_id: contractId, won: res.data.result.won });
            setRollResult({ configId: diceId, result: res.data });
            setRolling(false);
            queryClient.invalidateQueries({ queryKey: ['dice', contractId] });
            queryClient.invalidateQueries({ queryKey: ['contract', String(contractId)] });
            queryClient.invalidateQueries({ queryKey: ['ledger', String(contractId)] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
        },
        onError: () => setRolling(false),
    });

    const deleteMutation = useMutation({
        mutationFn: (diceId: number) => api.delete(`/contracts/${contractId}/dice/${diceId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dice', contractId] }),
    });

    const configs = diceData?.data ?? [];

    const handleRoll = (diceId: number) => {
        setRolling(true);
        setRollResult(null);
        rollMutation.mutate(diceId);
    };

    const handleDiceTypeChange = (type: string) => {
        const sides = DICE_TYPE_SIDES[type];
        setForm(f => ({
            ...f,
            dice_type: type,
            num_sides: sides ? String(sides) : f.num_sides,
        }));
    };

    const winConditionDescription = (config: DiceConfig) => {
        if (['doubles', 'evens', 'odds'].includes(config.win_condition)) {
            return WIN_CONDITION_LABELS[config.win_condition];
        }
        return `${WIN_CONDITION_LABELS[config.win_condition]} ${config.win_threshold}`;
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">Dice Roll</CardTitle>
                {isDom && (
                    <Button size="sm" variant="outline" onClick={() => setShowCreate(!showCreate)}>
                        {showCreate ? 'Cancel' : '+ Add Dice'}
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {showCreate && (
                    <div className="space-y-3 rounded border border-slate-700 p-3">
                        <Input
                            placeholder="Name (e.g. Double or Nothing)"
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        />
                        <div>
                            <label className="text-xs text-slate-400">Mode</label>
                            <select
                                className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm text-slate-200"
                                value={form.mode}
                                onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}
                            >
                                <option value="non_cumulative">Standard</option>
                                <option value="cumulative">Cumulative (Sub vs Dom)</option>
                                <option value="unlimited">Unlimited</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs text-slate-400">Dice Type</label>
                                <select
                                    className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm text-slate-200"
                                    value={form.dice_type}
                                    onChange={e => handleDiceTypeChange(e.target.value)}
                                >
                                    <option value="d4">d4 (4 sides)</option>
                                    <option value="d6">d6 (6 sides)</option>
                                    <option value="d8">d8 (8 sides)</option>
                                    <option value="d10">d10 (10 sides)</option>
                                    <option value="d12">d12 (12 sides)</option>
                                    <option value="d20">d20 (20 sides)</option>
                                    <option value="custom">Custom</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">Number of Dice</label>
                                <Input type="number" min="1" max="10" value={form.num_dice} onChange={e => setForm(f => ({ ...f, num_dice: e.target.value }))} />
                            </div>
                        </div>
                        {form.dice_type === 'custom' && (
                            <div>
                                <label className="text-xs text-slate-400">Sides per Die</label>
                                <Input type="number" min="2" max="100" value={form.num_sides} onChange={e => setForm(f => ({ ...f, num_sides: e.target.value }))} />
                            </div>
                        )}
                        {form.mode !== 'cumulative' && (
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs text-slate-400">Win Condition</label>
                                    <select
                                        className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm text-slate-200"
                                        value={form.win_condition}
                                        onChange={e => setForm(f => ({ ...f, win_condition: e.target.value }))}
                                    >
                                        <option value="above">Above threshold</option>
                                        <option value="below">Below threshold</option>
                                        <option value="exact">Exact match</option>
                                        <option value="doubles">Doubles</option>
                                        <option value="evens">Even total</option>
                                        <option value="odds">Odd total</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400">Threshold</label>
                                    <Input type="number" min="0" value={form.win_threshold} onChange={e => setForm(f => ({ ...f, win_threshold: e.target.value }))} />
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs text-slate-400">Loss Amount ($)</label>
                                <Input type="number" min="0" step="0.01" value={form.loss_amount} onChange={e => setForm(f => ({ ...f, loss_amount: e.target.value }))} />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">Win Reward ($)</label>
                                <Input type="number" min="0" step="0.01" value={form.win_reward} onChange={e => setForm(f => ({ ...f, win_reward: e.target.value }))} />
                            </div>
                        </div>
                        {form.mode === 'cumulative' && (
                            <div>
                                <label className="text-xs text-slate-400">Multiplier Amount ($)</label>
                                <p className="text-xs text-slate-500 mb-1">Fee = |difference| x multiplier</p>
                                <Input type="number" min="0" step="0.01" value={form.multiplier_amount} onChange={e => setForm(f => ({ ...f, multiplier_amount: e.target.value }))} placeholder="1.00" />
                            </div>
                        )}
                        <div>
                            <label className="text-xs text-slate-400">Cooldown (minutes)</label>
                            <Input type="number" min="0" value={form.cooldown_minutes} onChange={e => setForm(f => ({ ...f, cooldown_minutes: e.target.value }))} />
                        </div>
                        <Button
                            size="sm"
                            disabled={!form.name || createMutation.isPending}
                            onClick={() => createMutation.mutate({
                                name: form.name,
                                num_dice: parseInt(form.num_dice),
                                num_sides: parseInt(form.num_sides),
                                win_condition: form.win_condition,
                                win_threshold: parseInt(form.win_threshold),
                                loss_amount: parseFloat(form.loss_amount),
                                win_reward: parseFloat(form.win_reward),
                                cooldown_minutes: parseInt(form.cooldown_minutes) || 0,
                                mode: form.mode,
                                dice_type: form.dice_type,
                                ...(form.multiplier_amount ? { multiplier_amount: parseFloat(form.multiplier_amount) } : {}),
                            })}
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create Dice Game'}
                        </Button>
                    </div>
                )}

                {configs.length === 0 && !showCreate && (
                    <p className="text-sm text-slate-500 italic">No dice games configured.</p>
                )}

                {configs.map(config => (
                    <div key={config.id} className="rounded border border-slate-700 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm">{config.name}</span>
                                <Badge variant={config.is_active ? 'default' : 'secondary'}>
                                    {config.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                                {config.mode !== 'non_cumulative' && (
                                    <Badge variant="outline" className="text-[10px]">
                                        {MODE_LABELS[config.mode]}
                                    </Badge>
                                )}
                            </div>
                            {isDom && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-400 text-xs shrink-0"
                                    onClick={() => deleteMutation.mutate(config.id)}
                                >
                                    Delete
                                </Button>
                            )}
                        </div>
                        <div className="text-xs text-slate-400 space-y-0.5">
                            <p>
                                {config.num_dice} {config.num_dice === 1 ? 'die' : 'dice'}, {config.dice_type || `d${config.num_sides}`}
                                {config.mode !== 'cumulative' && ` — ${winConditionDescription(config)}`}
                                {config.mode === 'cumulative' && ' — Sub vs Dom roll'}
                            </p>
                            <p>
                                {config.mode === 'cumulative' && config.multiplier_amount
                                    ? `Multiplier: ${formatCurrency(parseFloat(config.multiplier_amount))} per point difference`
                                    : (
                                        <>
                                            Loss: +{formatCurrency(parseFloat(config.loss_amount))}
                                            {parseFloat(config.win_reward) > 0 && ` · Win: -${formatCurrency(parseFloat(config.win_reward))}`}
                                        </>
                                    )}
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-800 text-blue-400 hover:bg-blue-900/30"
                                disabled={rolling || !config.is_active}
                                onClick={() => handleRoll(config.id)}
                            >
                                {rolling && rollMutation.variables === config.id ? 'Rolling...' : 'Roll!'}
                            </Button>
                            {config.cooldown_minutes > 0 && (
                                <span className="text-xs text-slate-500">Cooldown: {config.cooldown_minutes}m</span>
                            )}
                        </div>
                        {rollResult?.configId === config.id && (
                            <div className={`rounded p-3 text-center ${rollResult.result.result.won ? 'bg-green-900/30' : 'bg-red-900/30'}`}>
                                {config.mode === 'cumulative' && rollResult.result.result.dom_dice ? (
                                    <>
                                        <div className="mb-2">
                                            <p className="text-xs text-slate-400 mb-1">Your Roll</p>
                                            <div className="flex flex-wrap justify-center gap-2">
                                                {rollResult.result.result.dice.map((d, i) => (
                                                    <DieFace key={i} value={d} won={rollResult.result.result.won} />
                                                ))}
                                            </div>
                                            <p className="text-sm text-slate-300">Total: <span className="font-bold">{rollResult.result.result.total}</span></p>
                                        </div>
                                        <div className="mb-2">
                                            <p className="text-xs text-slate-400 mb-1">Dom's Roll</p>
                                            <div className="flex flex-wrap justify-center gap-2">
                                                {rollResult.result.result.dom_dice.map((d, i) => (
                                                    <DieFace key={i} value={d} won={!rollResult.result.result.won} />
                                                ))}
                                            </div>
                                            <p className="text-sm text-slate-300">Total: <span className="font-bold">{rollResult.result.result.dom_total}</span></p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex flex-wrap justify-center gap-2 mb-2">
                                            {rollResult.result.result.dice.map((d, i) => (
                                                <DieFace key={i} value={d} won={rollResult.result.result.won} />
                                            ))}
                                        </div>
                                        <p className="text-sm text-slate-300 mb-1">Total: <span className="font-bold">{rollResult.result.result.total}</span></p>
                                    </>
                                )}
                                <p className={`text-xl font-bold ${rollResult.result.result.won ? 'text-green-400' : 'text-red-400'}`}>
                                    {rollResult.result.result.won ? 'WIN!' : 'LOSS'}
                                </p>
                                {rollResult.result.result.amount > 0 && (
                                    <p className="text-sm text-slate-400 mt-1">
                                        {rollResult.result.result.won
                                            ? `-${formatCurrency(rollResult.result.result.amount)} off your balance`
                                            : `+${formatCurrency(rollResult.result.result.amount)} added to balance`}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
